import {
  Box,
  LinearProgress,
  linearProgressClasses,
  Stack,
  styled,
} from "@mui/material";
import { ReactNode } from "react";
import { Body1Typography } from "../../../components/typography";
import CheckIcon from "@mui/icons-material/Check";

interface MatchingProfileStepsProps {
  userType?: "homeowner" | "designer";
  currentStep?: number;
  children?: ReactNode;
  showProgress?: boolean;
}

const designerSteps = [
  { title: "Personality" },
  { title: "Expertise" },
  { title: "Design Style" },
  { title: "Work Style" },
  { title: "Preferences" },
];

const homeownerSteps = [
  { title: "Personality" },
  { title: "Design Style" },
  { title: "Project" },
  { title: "Preferences" },
  { title: "Priorities" },
];

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#4F46E5",
  },
}));

export const MatchingProfileSteps = ({
  userType = "designer",
  currentStep = 1,
  children,
  showProgress = true,
}: MatchingProfileStepsProps) => {
  const stepIndex = currentStep - 1;
  const steps = userType === "homeowner" ? homeownerSteps : designerSteps;
  const currentStepInfo = steps[stepIndex];
  const isComplete = currentStep > steps.length;
  const showHeader = showProgress && !isComplete;

  return (
    <Box
      className="matching-profile-steps content-block"
      p={{ xs: 2, md: 4 }}
      sx={{
        // Mobile: full-width white background
        bgcolor: "background.paper",
        borderRadius: { xs: 0, md: 3 },
        boxShadow: { xs: "none", md: 1 },
        mx: { xs: -2, md: "auto" }
      }}
    >
      {showHeader && (
        <Box position="relative">
        {/* Desktop view - Full progress bar with all titles */}
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Stack direction="row">
            {steps.map((step, index) => (
              <Box
                key={index}
                className="matching-profile-step"
                width="100%"
                textAlign="center"
                position="relative"
                zIndex={5}
              >
                <Box
                  sx={{
                    borderRadius: "50%",
                    background: (theme) =>
                      stepIndex > index
                        ? (theme.palette as any).purple.main
                        : "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 40,
                    height: 40,
                    mx: "auto",
                    border: "1px solid",
                    borderColor: (theme) =>
                      stepIndex === index
                        ? (theme.palette as any).purple.main
                        : theme.palette.divider,
                  }}
                >
                  {stepIndex > index ? (
                    <CheckIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#fff",
                      }}
                    />
                  ) : (
                    <Body1Typography
                      sx={{
                        color: (theme) =>
                          stepIndex === index
                            ? (theme.palette as any).purple.main
                            : theme.palette.text.secondary,
                      }}
                    >
                      {index + 1}
                    </Body1Typography>
                  )}
                </Box>
                <Body1Typography
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translate(-50%, 100%)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.title}
                </Body1Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Mobile view - Dots only, title below */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Stack direction="row">
            {steps.map((step, index) => (
              <Box
                key={index}
                className="matching-profile-step"
                width="100%"
                textAlign="center"
                position="relative"
                zIndex={5}
              >
                <Box
                  sx={{
                    borderRadius: "50%",
                    background: (theme) =>
                      stepIndex > index
                        ? (theme.palette as any).purple.main
                        : "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 28,
                    height: 28,
                    mx: "auto",
                    border: "1px solid",
                    borderColor: (theme) =>
                      stepIndex === index
                        ? (theme.palette as any).purple.main
                        : theme.palette.divider,
                  }}
                >
                  {stepIndex > index ? (
                    <CheckIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#fff",
                      }}
                    />
                  ) : (
                    <Body1Typography
                      sx={{
                        color: (theme) =>
                          stepIndex === index
                            ? (theme.palette as any).purple.main
                            : theme.palette.text.secondary,
                      }}
                    >
                      {index + 1}
                    </Body1Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>

          {currentStepInfo && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
                borderRadius: 1,
              }}
            >
              <Body1Typography>{currentStepInfo.title}</Body1Typography>
            </Box>
          )}
        </Box>

        {/* Progress bar */}
        <BorderLinearProgress
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            transform: "translateY(-50%)",
          }}
          value={
            steps.length === currentStep
              ? (currentStep / steps.length) * 100
              : steps.length > 0
              ? ((currentStep - 0.5) / steps.length) * 100
              : 0
          }
          variant="determinate"
        />
      </Box>
      )}

      {children && <Box mt={6}>{children}</Box>}
    </Box>
  );
};
